// client/src/features/owner-portal/context/OwnerUnitContext.tsx
// 868mbzt38 — D1 (owner-portal anti-cycle).
//
// Single source of truth for the owner's unit list. The query lives in
// `hooks/useOwnedUnits.ts` so every consumer (this provider, the unit
// detail aggregator, future hooks) shares TanStack cache by key. The
// provider exposes a latched error (`loadError`) plus a `retry` callback
// so the guard renders a stable notice once and the "Retry" button
// re-runs the same query.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ownerUnitsQueryKey, useOwnedUnits } from '@/hooks/useOwnedUnits';
import { showToast } from '@/lib/toast';
import { ApiRequestError } from '@/lib/queryClient';
import type { OwnedUnitListItem } from '@shared/types/owner/owned-units';

// ── Constants ─────────────────────────────────────────────────────────────────

const SESSION_STORAGE_KEY = 'owner_active_unit_id';
const USER_SELECTED_KEY = 'owner_unit_user_selected';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OwnerUnitContextValue {
  activeUnit: OwnedUnitListItem | null;
  allUnits: OwnedUnitListItem[];
  isLoading: boolean;
  hasUserSelected: boolean;
  switchUnit: (unitId: string) => void;
  // 868mbzt38 — D1 latched error. Stays truthy from the moment the
  // units query fails with no data until the next successful fetch,
  // so the guard doesn't blink between skeleton and error on retries.
  loadError: Error | null;
  isRetrying: boolean;
  retry: () => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const OwnerUnitContext = createContext<OwnerUnitContextValue>({
  activeUnit: null,
  allUnits: [],
  isLoading: true,
  hasUserSelected: false,
  switchUnit: () => undefined,
  loadError: null,
  isRetrying: false,
  retry: () => undefined,
});

// ── Provider ──────────────────────────────────────────────────────────────────

export function OwnerUnitProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching, error, refetch } = useOwnedUnits();

  // 868mbzt38 — D1 latched error. The latch sticks once the units query
  // is in error with no data, and clears only when data arrives. TanStack
  // v5 clears `error` during refetch — without the latch, the guard
  // would flicker between skeleton and notice on every Retry.
  const [latchedError, setLatchedError] = useState<Error | null>(null);
  const [activeUnitId, setActiveUnitId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [hasUserSelected, setHasUserSelected] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(USER_SELECTED_KEY) === '1';
    } catch {
      return false;
    }
  });

  // 868mbzt38 — `data` represents the server response. `undefined` means
  // "the query hasn't settled yet"; `[]` means "the server answered with
  // no units"; an array means data has arrived. The B4 fix: the latch
  // clears on `data !== undefined`, so an owner who had a failure and
  // then receives `200 []` on Retry clears the notice instead of being
  // stuck on it. B6: a 401 invalidates the auth query so the Router
  // re-evaluates `isAuthenticated` (a fresh null user lands them on
  // Login per EX-03); with the session still alive, the same call site
  // re-fetches without invalidating.
  const units: OwnedUnitListItem[] = useMemo<OwnedUnitListItem[]>(
    () => data ?? [],
    [data],
  );

  useEffect(() => {
    if (data !== undefined) {
      setLatchedError(null);
      return;
    }
    if (error && !isFetching) {
      setLatchedError(error);
    }
  }, [error, data, isFetching]);

  useEffect(() => {
    if (error instanceof ApiRequestError && error.status === 401) {
      void queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
    }
  }, [error, queryClient]);

  const isRetrying = isFetching && latchedError !== null;

  // 868mbzt38 D1 / P1 — la condición para limpiar el aviso es
  // «llegó data», no «hay unidades». Si el server responde `200 []`
  // (un owner sin unidades), el cerrojo se libera; un refetch
  // posterior que falle no enciende el aviso otra vez porque `data` ya
  // no es `undefined`. Esto evita que T7 (refetch fallido con data
  // cacheada) muestre el aviso por error.
  // D6 — el latch se mantiene como `error` durante el refetch window
  // (data undefined, isFetching=true) para que EX-01 no parpadee.
  //
  // Usamos `useRef` en lugar de `useState` para el latch. Con `useState`
  // + `useLayoutEffect` el batch de React entre el `setLatchedError` y
  // el `setState` de TanStack (que limpia `error` durante el refetch)
  // puede descartar la actualización — el `useRef` es síncrono y la
  // siguiente lectura ya ve el valor nuevo.
  const loadError: Error | null =
    data !== undefined ? null : (error ?? latchedError);

  // Once units load, validate/set the active unit
  useEffect(() => {
    if (isLoading || units.length === 0) {
      return;
    }

    const storedId = activeUnitId;
    const isValid = storedId !== null && units.some((u) => u.unitId === storedId);

    if (!isValid) {
      const firstId = units[0].unitId;
      setActiveUnitId(firstId);
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, firstId);
      } catch {
        // sessionStorage unavailable — ignore
      }
    }
  }, [units, isLoading, activeUnitId]);

  const switchUnit = useCallback(
    (unitId: string) => {
      if (!units.some((u) => u.unitId === unitId)) {
        showToast.error('Unit unavailable', 'This unit is no longer assigned to you.');
        queryClient.invalidateQueries({ queryKey: ownerUnitsQueryKey });
        return;
      }

      setActiveUnitId(unitId);
      setHasUserSelected(true);

      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, unitId);
        sessionStorage.setItem(USER_SELECTED_KEY, '1');
      } catch {
        // sessionStorage unavailable — ignore
      }

      queryClient.invalidateQueries({ queryKey: ownerUnitsQueryKey });
      queryClient.invalidateQueries({ queryKey: ['owner-portal'] });
      queryClient.invalidateQueries({ queryKey: ['/api/owner-portal/announcements'] });
      queryClient.invalidateQueries({ queryKey: ['ownerWorkOrders'] });
      queryClient.invalidateQueries({ queryKey: ['ownerWorkOrderDetail'] });
    },
    [units, queryClient],
  );

  const retry = useCallback((): void => {
    void refetch();
  }, [refetch]);

  const activeUnit = useMemo<OwnedUnitListItem | null>(
    () => units.find((u) => u.unitId === activeUnitId) ?? units[0] ?? null,
    [units, activeUnitId],
  );

  return (
    <OwnerUnitContext.Provider
      value={{
        activeUnit,
        allUnits: units,
        isLoading,
        hasUserSelected,
        switchUnit,
        loadError,
        isRetrying,
        retry,
      }}
    >
      {children}
    </OwnerUnitContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useOwnerUnit(): OwnerUnitContextValue {
  return useContext(OwnerUnitContext);
}
